import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
	GovLayout,
	GovLayoutColumn,
	GovTag,
} from '@gov-design-system-ce/react';
import { LocalizeNameSpaceTypes, useCmsPage } from '@gov-nx/core/service';
import type { PageCode } from '@gov-nx/module/page';
import type { BreadcrumbOption } from '@gov-nx/ui/web';
import { BackButton, Breadcrumbs, ToggleButton } from '@gov-nx/ui/web';
import { useTitle } from '@gov-nx/utils/web';
import { FavoriteToggle } from '../Favorite/FavoriteToggle';
import { MarkdownRender } from '../Markdown/MarkdownRender';
import { CmsFaq } from './CmsFaq';
import { CsmNavigationSignpost } from './CsmNavigationSignpost';
import type { CmsNavigation } from '@gov-nx/api';

export interface CmsPageRenderProps {
	code: PageCode;
	children: React.ReactNode;
	showDescription?: boolean;
	showFavorite?: boolean;
	showBackButton?: boolean;
	breadcrumbs?: { options: BreadcrumbOption[]; isLoading: boolean };
	override?: {
		name?: string;
		description?: string;
		navigations?: CmsNavigation[];
	};
	tag?: string;
}

export const CmsPageRender = ({
	code,
	children,
	showDescription = true,
	showFavorite = true,
	showBackButton = true,
	breadcrumbs,
	tag,
	override = {},
}: CmsPageRenderProps) => {
	const navigate = useNavigate();
	const cmsContent = useCmsPage({ code });
	const localizeNameSpace = LocalizeNameSpaceTypes.Page;
	const title =
		override?.name ?? cmsContent?.overrideName ?? cmsContent?.name ?? null;

	const description = override?.description ?? cmsContent?.description ?? null;
	const navigations = override?.navigations ?? cmsContent?.navigations ?? null;

	useTitle(title);

	if (cmsContent === null) {
		return null;
	}

	return (
		<>
			<GovLayout
				type="text"
				variant="left"
				className="pb-(--templates-margin-3xl)">
				<GovLayoutColumn>
					<main className="flex flex-col gap-(--templates-margin-3xl)!">
						<header className="flex flex-col gap-(--templates-margin-l)">
							{showBackButton ? (
								<div className="print:hidden">
									<BackButton onClick={() => navigate(-1)} />
								</div>
							) : null}
							{!breadcrumbs?.isLoading && breadcrumbs?.options ? (
								<Breadcrumbs options={breadcrumbs.options} />
							) : null}
							<h1>
								{title}
								{cmsContent.favoriteAllowed && showFavorite ? (
									<FavoriteToggle
										{...{ code, name: `${title}` }}
										customClass="relative top-[-7px]"
									/>
								) : null}
							</h1>
							{tag ? (
								<GovTag
									color="neutral"
									type="subtle"
									size="m"
									className="w-fit">
									{tag}
								</GovTag>
							) : null}
							{description && showDescription ? (
								cmsContent.descriptionDisplayed ? (
									<MarkdownRender>{description}</MarkdownRender>
								) : (
									<ToggleButton className="hover-reverse">
										<MarkdownRender>{description}</MarkdownRender>
									</ToggleButton>
								)
							) : null}
						</header>
						{children}
					</main>
				</GovLayoutColumn>
			</GovLayout>
			{navigations.length ? (
				<CsmNavigationSignpost
					navigations={navigations}
					localizeNameSpace={localizeNameSpace}
				/>
			) : null}
			{cmsContent.faq.length ? (
				<CmsFaq
					faq={cmsContent.faq}
					localizeNameSpace={localizeNameSpace}
				/>
			) : null}
		</>
	);
};
